import type { Collection } from '@prisma/client'
import type { SerializeFrom } from '@remix-run/server-runtime'
import { GripVerticalIcon } from 'lucide-react'
import { Reorder } from 'motion/react'
import { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar as ThumbnailEmptyState } from '~/components/EmptyState'
import { Media } from '~/components/Media/Media'
import { ShopReorderContext } from '~/components/ShopReorder'
import { SideDrawer } from '~/components/SideDrawer'
import { makeShopOwnPath } from '~/helpers/routes'
import { useShop } from '~/hooks/data/use-shop'
import { Alert } from '~/ui'

const CollectionsReorderDrawerContent = () => {
  const { t } = useTranslation()
  const shop = useShop()
  const { open, setOpen, collections, updateCollections, resetCollections } =
    useContext(ShopReorderContext)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const orderedCollections = useMemo(() => collections, [collections])

  const handleReorder = async (newOrder: string[]) => {
    const reorderedCollections = newOrder
      .map((id) => orderedCollections.find((c) => c.id === id)!)
      .filter(Boolean)

    const result = reorderedCollections.map((c, index) => ({
      id: c.id,
      disposition: reorderedCollections.length - index,
    }))

    updateCollections(reorderedCollections)

    try {
      const response = await fetch(
        makeShopOwnPath(shop, '/collections/reorder'),
        {
          method: 'PATCH',
          body: JSON.stringify({ collections: result }),
        }
      )
      if (!response.ok) {
        throw new Error(response.statusText)
      }

      await response.json()
    } catch (error) {
      console.error(error)
      resetCollections()
      setErrorMessage(t('errors.reorder_collections_error'))
    }
  }

  return (
    <SideDrawer
      open={open === 'collections'}
      onOpenChange={(isOpen) => setOpen(isOpen ? 'collections' : null)}
      className="text-foreground"
    >
      <SideDrawer.Header
        title={t('action.reorder_collections')}
        description={t('action.reorder_collections')}
      />
      <SideDrawer.Content>
        {errorMessage && (
          <Alert className="mb-3" type="error" title={errorMessage} />
        )}

        <Reorder.Group
          axis="y"
          values={orderedCollections.map((c) => c.id)}
          onReorder={handleReorder}
          className="flex list-none flex-col gap-2"
        >
          {orderedCollections.map((collection) => (
            <Reorder.Item
              key={collection.id}
              value={collection.id}
              className="shrink-0"
            >
              <CollectionReorderItem collection={collection} />
            </Reorder.Item>
          ))}
        </Reorder.Group>
      </SideDrawer.Content>
    </SideDrawer>
  )
}

export const CollectionsReorderDrawer = () => {
  const context = useContext(ShopReorderContext)

  if (!context) {
    return null
  }

  return <CollectionsReorderDrawerContent />
}

const CollectionReorderItem = ({
  collection,
}: {
  collection: SerializeFrom<Collection>
}) => {
  return (
    <div className="bg-background border-border/4 flex items-center gap-2.5 rounded-xl border p-4 hover:cursor-move">
      {collection.avatarSrc ? (
        <div className="size-11 shrink-0 overflow-hidden rounded-md">
          <Media
            src={collection.avatarSrc}
            kind="image"
            className="h-full w-full object-cover"
            transformation={{
              width: 100,
              format: 'webp',
              resizeMode: 'fill',
            }}
            sizes={{
              default: '45px',
            }}
          />
        </div>
      ) : (
        <div className="size-11 shrink-0 overflow-hidden rounded-md">
          <ThumbnailEmptyState className="opacity-64" />
        </div>
      )}

      <div className="flex min-w-0 flex-1">
        <p className="line-clamp-1 truncate text-sm font-medium">
          {collection.name}
        </p>
      </div>

      <GripVerticalIcon className="ml-auto size-3 shrink-0 opacity-40" />
    </div>
  )
}
